import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useRef } from "react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { useSlug, slugify } from "@/hooks/useSlug";

const schema = z.object({
  title: z.string().min(2).max(200),
  slug: z.string().min(1).max(120).regex(/^[a-z0-9-]+$/),
  category: z.string().max(60).optional(),
});
type FormValues = z.infer<typeof schema>;

export const Route = createFileRoute("/_app/content/news/new")({ component: NewArticle });

function NewArticle() {
  const navigate = useNavigate();
  const slugTouched = useRef(false);
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema), defaultValues: { title: "", slug: "", category: "" },
  });
  useSlug({
    title: watch("title"), currentSlug: watch("slug"),
    setSlug: (s) => setValue("slug", s, { shouldValidate: true }),
    slugTouched,
  });
  const create = useMutation({
    mutationFn: (v: FormValues) => api.post<{ id: number }>("/admin/news", v),
    onSuccess: (r) => {
      toast.success("Draft created");
      navigate({ to: "/content/news/$id" as never, params: { id: String(r.id) } });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Create failed"),
  });

  return (
    <div className="max-w-2xl">
      <PageHeader title="New article" back={{ href: "/content/news", label: "News" }} />
      <Card>
        <CardContent className="pt-6">
          <form className="space-y-4" onSubmit={handleSubmit((v) => create.mutate(v))}>
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input id="title" placeholder="KRCT wins national hackathon…" {...register("title")} />
              {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">URL slug</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">/news/</span>
                <Input
                  id="slug"
                  {...register("slug", { onChange: () => { slugTouched.current = true; } })}
                  onBlur={(e) => setValue("slug", slugify(e.target.value), { shouldValidate: true })}
                />
              </div>
              {errors.slug && <p className="text-xs text-destructive">{errors.slug.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category (optional)</Label>
              <Input id="category" placeholder="Placement / Research / Achievement" {...register("category")} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => navigate({ to: "/content/news" as never })}>Cancel</Button>
              <Button type="submit" disabled={create.isPending}>{create.isPending ? "Creating…" : "Create article"}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
